import React, { useRef, useState } from 'react';
import { Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/** Prüft eine Datei gegen ein accept-Attribut (".pdf,image/*,application/pdf"). */
export const acceptsFile = (file, accept) => {
  if (!accept) return true;
  const name = (file.name || '').toLowerCase();
  const type = (file.type || '').toLowerCase();
  return accept.split(',').map(t => t.trim().toLowerCase()).filter(Boolean).some(t => {
    if (t.startsWith('.')) return name.endsWith(t);
    if (t.endsWith('/*')) return type.startsWith(t.slice(0, -1));
    return type === t;
  });
};

/**
 * Einheitliche Dropzone für alle Datei-Uploads: Klick öffnet den Dateidialog, Drag & Drop (z. B. aus Outlook) lädt direkt.
 * variant="block" (gestrichelte Fläche) oder "inline" (gestrichelter Button). onFiles erhält immer ein Array von Files.
 */
export const FileDropzone = ({
  onFiles, accept, multiple = false, busy = false, disabled = false,
  variant = 'block', size = 'md', label, hint, busyLabel = 'Lädt …', icon: Icon = Upload,
  testId = 'file-dropzone', inputTestId, className = '', children,
}) => {
  const inputRef = useRef();
  const depth = useRef(0);
  const [over, setOver] = useState(false);
  const inactive = busy || disabled;

  const emit = (list) => {
    let files = Array.from(list || []);
    if (!files.length) return;
    const bad = files.filter(f => !acceptsFile(f, accept));
    if (bad.length) {
      toast.error(bad.length === files.length ? `Dateityp nicht erlaubt${hint ? ` – ${hint}` : ''}` : `${bad.length} Datei(en) übersprungen (Dateityp nicht erlaubt)`);
      files = files.filter(f => acceptsFile(f, accept));
      if (!files.length) return;
    }
    onFiles(multiple ? files : files.slice(0, 1));
  };
  const onDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); if (inactive) return; depth.current += 1; setOver(true); };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); if (!inactive && !over) setOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); depth.current = Math.max(0, depth.current - 1); if (depth.current === 0) setOver(false); };
  const onDrop = (e) => { e.preventDefault(); e.stopPropagation(); depth.current = 0; setOver(false); if (!inactive) emit(e.dataTransfer?.files); };
  const open = () => { if (!inactive) inputRef.current?.click(); };
  const onKey = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } };

  const input = (
    <input ref={inputRef} type="file" accept={accept} multiple={multiple} className="hidden" disabled={inactive}
      data-testid={inputTestId || `${testId}-input`} onChange={e => { emit(e.target.files); e.target.value = ''; }} />
  );
  const common = { role: 'button', tabIndex: 0, 'data-testid': testId, 'data-dragover': over || undefined, onClick: open, onKeyDown: onKey, onDragEnter, onDragOver, onDragLeave, onDrop };

  if (variant === 'inline') {
    return (
      <span {...common}
        className={`inline-flex items-center gap-1.5 border border-dashed rounded-lg px-3 py-1.5 text-xs font-medium cursor-pointer select-none transition-colors ${over ? 'border-brand-gold bg-brand-gold/10 text-brand-gold' : 'border-gray-300 text-brand-navy hover:border-brand-navy/50 hover:bg-gray-50'} ${inactive ? 'opacity-60 pointer-events-none' : ''} ${className}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
        <span>{busy ? busyLabel : (children || label)}</span>
        {input}
      </span>
    );
  }

  const sm = size === 'sm';
  return (
    <div {...common}
      className={`rounded-lg border-2 border-dashed text-center cursor-pointer select-none transition-colors ${sm ? 'py-2.5 px-3' : 'py-5 px-4'} ${over ? 'border-brand-gold bg-brand-gold/10' : 'border-gray-200 hover:border-brand-navy/40 hover:bg-gray-50'} ${inactive ? 'opacity-60 pointer-events-none' : ''} ${className}`}>
      {busy ? <Loader2 className={`mx-auto mb-1 text-brand-navy animate-spin ${sm ? 'w-4 h-4' : 'w-6 h-6'}`} />
        : <Icon className={`mx-auto mb-1 ${sm ? 'w-4 h-4' : 'w-6 h-6'} ${over ? 'text-brand-gold' : 'text-brand-navy/60'}`} />}
      {children || (
        <>
          <p className={`${sm ? 'text-[11px]' : 'text-sm'} font-medium ${over ? 'text-brand-gold' : 'text-brand-navy'}`}>
            {busy ? busyLabel : over ? 'Jetzt loslassen' : label}
          </p>
          {!over && !busy && hint && <p className={`${sm ? 'text-[10px]' : 'text-xs'} text-gray-400 mt-0.5`}>{hint}</p>}
        </>
      )}
      {input}
    </div>
  );
};

export default FileDropzone;
